import { createPortal } from 'react-dom';
import { KebabMenuProps } from './types';
import { VISIBILITY_MENU_OPTIONS } from '../../../utils/visibilityLabels';
import {
  DOWNLOAD_INTERNAL_ONLY,
  PROCESSING_DELETE_INTERNAL_ONLY,
  PUBLISH_REQUIRES_SPECIALTY,
} from '../../../constants/accessMessages';
import styles from './KebabMenu.module.css';
import kebabMenuIcon from './kebab-menu-icon.svg';

/**
 * ケバブメニューコンポーネント
 * 動画カードなどで使用する3点リーダーメニューを提供
 */
export const KebabMenu = ({
  isOpen,
  position,
  onClose,
  isFavorite,
  isOwner,
  isPrivate,
  currentVisibility,
  isProcessing = false,
  hideVisibilityToggle = false,
  hideFavorite = false,
  hideEdit = false,
  isExternal = false,
  hasSpecialty = true,
  onFavoriteClick,
  onVisibilityClick,
  onEditClick,
          onDownloadClick,
          onDeleteClick,
          onCopyLinkClick,
          isCopied = false,
          onCommentClick,
          onCommentWithTimeClick,
          menuRef,
          buttonRef,
          onToggle,
          testIdPrefix = 'kebab-menu'
}: KebabMenuProps) => {
  const resolvedVisibility = currentVisibility || (isPrivate ? 'private' : 'public');
  const visibilityOptions = VISIBILITY_MENU_OPTIONS[resolvedVisibility];

  // 遠隔時はDLを disabled 表示するため、onDownloadClick の有無は isExternal に関わらず判定に含める
  const hasAnyItem =
    !hideFavorite ||
    (isOwner && !hideVisibilityToggle) ||
    (isOwner && !hideEdit) ||
    (isOwner && !!onDownloadClick && !isProcessing) ||
    !!onCopyLinkClick ||
    !!onCommentClick ||
    !!onCommentWithTimeClick ||
    (isOwner && !!onDeleteClick);

  return (
    <>
      {/* ケバブメニューボタン */}
      <button 
        ref={buttonRef}
        className={styles.kebabMenuButton}
        onClick={onToggle}
        aria-label="メニュー"
        data-testid={`${testIdPrefix}__button`}
      >
        <img src={kebabMenuIcon} alt="" aria-hidden="true" />
      </button>

      {/* ドロップダウンメニュー（Portal使用） */}
      {isOpen && createPortal(
        <div 
          className={styles.kebabDropdown} 
          ref={menuRef}
          style={{
            top: position.top,
            left: position.left,
          }}
          data-testid={`${testIdPrefix}__dropdown`}
        >
          {/* 表示できるアイテムがない場合のフォールバック */}
          {!hasAnyItem && (
            <p className={styles.kebabEmptyMessage}>表示できるコンテンツがありません</p>
          )}

          {/* お気に入りボタン（非表示設定でない場合） */}
          {!hideFavorite && (
            <button 
              onClick={onFavoriteClick}
              data-testid={`${testIdPrefix}__favorite-menu-item`}
            >
              {isFavorite ? 'お気に入り解除' : 'お気に入り登録'}
            </button>
          )}
          
          {/* 公開状態ボタン（オーナーのみ、かつ非表示設定でない場合）
              診療科未設定（hasSpecialty=false）のときは、公開範囲を広げる項目
              （院内公開にする／外部公開にする）を disabled + aria-disabled にし、
              title 属性で PUBLISH_REQUIRES_SPECIALTY 文言を表示する。
              「非公開にする」（公開を止める操作）は対象外のため活性のままにする。
              サーバー側の必須化チェック（requiresSpecialtyForVisibilityChange, videoController.ts）
              はこのUI抑止とは別に残る。 */}
          {isOwner && !hideVisibilityToggle && (
            visibilityOptions.map(option => {
              const disabledForSpecialty = option.visibility !== 'private' && !hasSpecialty;
              return disabledForSpecialty ? (
                <button
                  key={`visibility-${option.visibility}`}
                  disabled
                  aria-disabled="true"
                  title={PUBLISH_REQUIRES_SPECIALTY}
                  data-testid={`${testIdPrefix}__visibility-menu-item-${option.visibility}`}
                  className={styles.menuItemDisabled}
                >
                  {option.label}
                </button>
              ) : (
                <button
                  key={`visibility-${option.visibility}`}
                  onClick={(e) => onVisibilityClick(e, option.visibility)}
                  data-testid={`${testIdPrefix}__visibility-menu-item-${option.visibility}`}
                >
                  {option.label}
                </button>
              );
            })
          )}
          
          {/* 編集ボタン（オーナーのみ、かつ非表示設定でない場合） */}
          {isOwner && !hideEdit && (
            <button 
              onClick={onEditClick}
              data-testid={`${testIdPrefix}__edit-menu-item`}
            >
              編集
            </button>
          )}
          
          {/* ダウンロードボタン（オーナーのみ、処理中でない場合）
              遠隔（isExternal=true）時は disabled + aria-disabled にし、
              title 属性で DOWNLOAD_INTERNAL_ONLY 文言をホバー/フォーカス時に表示する（UX R1-a / R5）。
              disabled な button にはブラウザが click を dispatch しないため、
              onClick は付けない（付けても到達しないデッドコードになる）。 */}
          {isOwner && onDownloadClick && !isProcessing && (
            isExternal ? (
              <button
                disabled
                aria-disabled="true"
                title={DOWNLOAD_INTERNAL_ONLY}
                data-testid={`${testIdPrefix}__download-menu-item`}
                className={styles.menuItemDisabled}
              >
                ダウンロード
              </button>
            ) : (
              <button
                onClick={onDownloadClick}
                data-testid={`${testIdPrefix}__download-menu-item`}
              >
                ダウンロード
              </button>
            )
          )}

          {/* リンクをコピーボタン */}
          {onCopyLinkClick && (
            <button 
              onClick={onCopyLinkClick}
              data-testid={`${testIdPrefix}__copy-link-menu-item`}
            >
              {isCopied ? 'コピー済み' : 'リンクをコピー'}
            </button>
          )}
          
          {/* コメントボタン */}
          {onCommentClick && (
            <button 
              onClick={onCommentClick}
              data-testid={`${testIdPrefix}__comment-menu-item`}
            >
              コメント
            </button>
          )}
          
          {/* 時刻付きコメントボタン */}
          {onCommentWithTimeClick && (
            <button 
              onClick={onCommentWithTimeClick}
              data-testid={`${testIdPrefix}__comment-with-time-menu-item`}
            >
              時刻を付けてコメント
            </button>
          )}

          {/* 削除ボタン（オーナーのみ、最下部）
              処理中動画の削除は DELETE /api/videos/upload/:sessionId を叩くため
              backend の requireInternalNetwork('upload') に阻まれる。
              遠隔（isExternal=true）かつ処理中のときのみ disabled にし、
              title 属性で PROCESSING_DELETE_INTERNAL_ONLY 文言を表示する。
              変換完了済みの通常動画の削除は院外でも許可されているため活性のままにする。 */}
          {isOwner && onDeleteClick && (
            isExternal && isProcessing ? (
              <button
                disabled
                aria-disabled="true"
                title={PROCESSING_DELETE_INTERNAL_ONLY}
                data-testid={`${testIdPrefix}__delete-menu-item`}
                className={styles.menuItemDisabled}
              >
                削除
              </button>
            ) : (
              <button
                onClick={onDeleteClick}
                data-testid={`${testIdPrefix}__delete-menu-item`}
                className={styles.deleteButton}
              >
                削除
              </button>
            )
          )}
        </div>,
        document.body
      )}
    </>
  );
};
